import { __assign, __awaiter, __generator, __read } from "tslib";
import React, { useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { OtherTagAPIContext } from "@shared/contexts/api/OtherTagAPIContext";
import { getTagGroupCustomFieldsEntries, } from "@shared/models/tag";
import { OtherTagExtractors } from "@shared/models/other_tag";
import { ROUTES } from "@router/routes";
import { FormattedMessage } from "react-intl";
import { useLocation, useParams } from "react-router-dom";
import { useAppToast } from "../common/Toast";
import { TagDetailHeader } from "../settings/TagDetailHeader";
import { TagDetailInfo } from "../settings/TagDetailInfo";
import { useNavigation } from "@hooks/useNavigation";
import { ColouredTag } from "@components/common/ColouredTag";
import { TagGroupAPIContext } from "@shared/contexts/api/TagGroupAPIContext";
import { useMergeTagDialog, MergeTagDialog } from "../common/MergeTagDialog";
export var OtherTagDetailScreen = React.memo(function () {
    var _a, _b, _c, _d, _e, _f, _g;
    var tagId = useParams().tagId;
    var otherTagId = useMemo(function () { return parseInt(tagId, 10); }, [tagId]);
    var navigation = useNavigation();
    var location = useLocation();
    var showErrorToast = useAppToast().showErrorToast;
    var otherTagAPI = useContext(OtherTagAPIContext);
    var tagGroupAPI = useContext(TagGroupAPIContext);
    var _h = __read(useState(null), 2), otherTag = _h[0], setOtherTag = _h[1];
    var _j = __read(useState(false), 2), isLoading = _j[0], setIsLoading = _j[1];
    var _k = __read(useState([]), 2), availableCustomProperties = _k[0], setAvailableCustomProperties = _k[1];
    var _l = __read(useState((_b = (_a = location.state) === null || _a === void 0 ? void 0 : _a.tagName) !== null && _b !== void 0 ? _b : ""), 2), tagName = _l[0], setTagName = _l[1];
    var goGroupDetail = useCallback(function () {
        navigation.replace(ROUTES.settings.tags.others.group_detail);
    }, [navigation]);
    var fetchOtherTag = useCallback(function () {
        setIsLoading(true);
        Promise.all([
            otherTagAPI.getOtherTag(otherTagId),
            tagGroupAPI.getTagGroupBySlug("other_tag"),
        ])
            .then(function (_a) {
            var _b = __read(_a, 2), tag = _b[0], tagGroup = _b[1];
            setOtherTag(tag);
            setTagName(tag.name);
            setAvailableCustomProperties(getTagGroupCustomFieldsEntries(tagGroup.definition).map(function (_a) {
                var _b = __read(_a, 2), key = _b[0], value = _b[1];
                return ({
                    name: value.name,
                    value: tag.additional[key],
                });
            }));
        })
            .catch(function (err) {
            console.error("Failed to fetch data = ", err);
            showErrorToast({
                titleId: "common.errors.data.fetch_failed.title",
                descriptionId: "common.errors.data.fetch_failed.description",
            }, {
                position: "bottom-center",
            });
            goGroupDetail();
        })
            .finally(function () {
            setIsLoading(false);
        });
    }, [otherTagAPI, otherTagId, tagGroupAPI, showErrorToast, goGroupDetail]);
    var onEditTag = useCallback(function () {
        navigation.push(ROUTES.settings.tags.others.group_detail + "/" + otherTagId + "/edit");
    }, [navigation, otherTagId]);
    var onEnableTag = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, otherTagAPI.enableOtherTag(otherTagId)];
        });
    }); }, [otherTagId, otherTagAPI]);
    var onDisableTag = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, otherTagAPI.disableOtherTag(otherTagId)];
        });
    }); }, [otherTagId, otherTagAPI]);
    var onDeleteTag = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, otherTagAPI.deleteOtherTag(otherTagId).then(function () {
                    goGroupDetail();
                })];
        });
    }); }, [otherTagAPI, otherTagId, goGroupDetail]);
    var _m = useMergeTagDialog(), mergeDialogProps = _m.mergeDialogProps, openMergeDialog = _m.openMergeDialog, closeMergeDialog = _m.closeMergeDialog;
    var _o = __read(useState(false), 2), isMerging = _o[0], setIsMerging = _o[1];
    var onMergeTag = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var source, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, openMergeDialog()];
                case 1:
                    source = _a.sent();
                    if (!source) {
                        return [2 /*return*/];
                    }
                    setIsMerging(true);
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, 5, 6]);
                    return [4 /*yield*/, otherTagAPI.mergeOtherTag(otherTagId, source.id)];
                case 3:
                    _a.sent();
                    closeMergeDialog();
                    fetchOtherTag();
                    return [3 /*break*/, 6];
                case 4:
                    err_1 = _a.sent();
                    console.error("Failed to merge tag", err_1);
                    showErrorToast({
                        titleId: "merge_tag.error.title",
                        descriptionId: "merge_tag.error.description",
                    }, {
                        position: "bottom-center",
                    });
                    return [3 /*break*/, 6];
                case 5:
                    setIsMerging(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [
        openMergeDialog,
        otherTagAPI,
        otherTagId,
        closeMergeDialog,
        showErrorToast,
        fetchOtherTag,
    ]);
    useEffect(function () {
        fetchOtherTag();
    }, [fetchOtherTag]);
    return (React.createElement("div", { className: "flex flex-col min-w-0 flex-1" },
        React.createElement(MergeTagDialog, __assign({}, mergeDialogProps, { destinationId: otherTagId, tagName: tagName, fetch: otherTagAPI.listOtherTags, isMerging: isMerging, keyExtractor: OtherTagExtractors.id, labelExtractor: OtherTagExtractors.name })),
        React.createElement(TagDetailHeader, { typeId: "settings.tags.title.others", tagName: tagName, isTagEnabled: (_c = otherTag === null || otherTag === void 0 ? void 0 : otherTag.isEnabled) !== null && _c !== void 0 ? _c : true, onEdit: onEditTag, onEnable: onEnableTag, onDisable: onDisableTag, onDelete: onDeleteTag, onMerge: onMergeTag, onGoBack: goGroupDetail }),
        React.createElement("div", { className: "flex-1 mt-2 overflow-y-auto md:mt-0 md:p-4" },
            React.createElement(TagDetailInfo.Panel, { isLoading: isLoading },
                React.createElement(TagDetailInfo.Item, { title: React.createElement(FormattedMessage, { id: "settings.tags.others.form.display_name" }), value: (_d = otherTag === null || otherTag === void 0 ? void 0 : otherTag.fullName) !== null && _d !== void 0 ? _d : "" }),
                React.createElement(TagDetailInfo.Item, { title: React.createElement(FormattedMessage, { id: "settings.tags.others.form.color" }), value: React.createElement(ColouredTag, { tag: (_f = (_e = otherTag === null || otherTag === void 0 ? void 0 : otherTag.color) === null || _e === void 0 ? void 0 : _e.name) !== null && _f !== void 0 ? _f : "", colour: otherTag === null || otherTag === void 0 ? void 0 : otherTag.color }) }),
                React.createElement(TagDetailInfo.Item, { title: React.createElement(FormattedMessage, { id: "settings.tags.others.form.remarks" }), value: (_g = otherTag === null || otherTag === void 0 ? void 0 : otherTag.remarks) !== null && _g !== void 0 ? _g : "" }),
                availableCustomProperties.map(function (value, index) { return (React.createElement(TagDetailInfo.Item, { key: index, title: value.name, value: value.value })); })))));
});
